import { ChildrenProp, DivElementProps } from '../common-types';
import React, { CSSProperties, forwardRef, Ref } from 'react';

/**
 * Type of the refs.
 */
type RefType = Ref<HTMLDivElement>;

/**
 * Properties of box layout components - HBox and VBox.
 * @author agd
 */
export interface BoxLayoutProps
  extends Required<ChildrenProp>,
    Omit<DivElementProps, 'children' | 'style' | 'ref'> {
  /**
   * How the elements should be aligned.
   * See {@link https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content}
   * for a description of what each possibility does.
   */
  align?:
    | 'space-around'
    | 'space-between'
    | 'space-evenly'
    | 'stretch'
    | 'center'
    | 'end'
    | 'flex-end'
    | 'flex-start'
    | 'start'
    | 'left'
    | 'normal'
    | 'right';
  /**
   * Overflow behaviour.
   * See {@link https://developer.mozilla.org/en-US/docs/Web/CSS/overflow} for details.
   */
  overflow?: 'auto' | 'hidden' | 'scroll' | 'visible';
  /**
   * If the layout should be reversed.
   */
  reverse?: boolean;
  /**
   * Wrapping behaviour.
   * See {@link https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap} for details.
   */
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  /**
   * Inline CSS to add to the element.
   * Properties that are manipulated through other BoxLayout properties are omitted here.
   */
  style?: Omit<CSSProperties, 'flexWrap' | 'overflow' | 'justifyContent'>;
}

/**
 * Box layout component to create a layout of the given variant with the given styling props.
 * @param properties the props
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
const BoxLayout = forwardRef(function BoxLayout(
  {
    variant,
    reverse = false,
    className = '',
    children,
    style,
    wrap,
    overflow,
    align,
    ...props
  }: BoxLayoutProps & { variant: 'hbox' | 'vbox' },
  ref: RefType
): JSX.Element {
  const classes = [`ehs-${variant}`, reverse && 'reverse', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      style={{
        flexWrap: wrap,
        overflow: overflow,
        justifyContent: align,
        ...style
      }}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  );
});

/**
 * Simple HBox component for layouting purposes.
 *
 * @param props a {@link BoxLayoutProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export const HBox = forwardRef(function HBox(
  props: BoxLayoutProps,
  ref: RefType
): JSX.Element {
  return <BoxLayout variant="hbox" {...props} ref={ref} />;
});

/**
 * Simple VBox component for layouting purposes.
 *
 * @param props a {@link BoxLayoutProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export const VBox = forwardRef(function VBox(
  props: BoxLayoutProps,
  ref: RefType
): JSX.Element {
  return <BoxLayout variant="vbox" {...props} ref={ref} />;
});
